import 'cropperjs/dist/cropper.css';
import './index.scss';

import { Component, createRef } from 'react';
import Cropper, { ReactCropperElement } from 'react-cropper';

import t from 'utils/i18n';
import { BraxUncontrolledRangeField } from '../BraxUncontrolledForm';

type Shape = 'circle' | 'square' | 'rectangle';

export type BraxImageEditorProps = {
    src: string,
    maxWidth?: number,
    maxHeight?: number,
    onCrop: (file: Blob) => void
};

type BraxImageEditorState = {
    zoom: number,
    shape: Shape,
    aspectRatio: number
};

export default class BraxImageEditor extends Component<BraxImageEditorProps, BraxImageEditorState> {

    state: BraxImageEditorState = {
        zoom: 100,
        shape: 'square',
        aspectRatio: 1
    };

    cropperRef = createRef<ReactCropperElement>();

    shapes: Shape[] = ['circle', 'square', 'rectangle'];

    aspectRatio: { [K in Shape]: number } = {
        circle: 1,
        square: 1,
        rectangle: 1.25
    };

    init() {
        this.updateAspectRatio(this.state.shape);
    }

    setShape(shape: Shape) {
        this.setState({ shape });

        this.updateAspectRatio(shape);
    }

    updateAspectRatio(shape: Shape) {
        this.cropperRef.current?.cropper?.setAspectRatio(this.aspectRatio[shape]);

        if (shape === 'rectangle')
            this.cropperRef.current?.cropper?.setAspectRatio(Number.NaN);
    }

    zoom(zoom: number) {
        this.setState({ zoom });
    }

    async save() {
        if (!this.cropperRef.current || !this.cropperRef.current.cropper)
            return;

        const croppedCanvas = this.cropperRef.current.cropper.getCroppedCanvas({
            maxWidth: this.props.maxWidth,
            maxHeight: this.props.maxHeight
        });

        const { width, height } = croppedCanvas;

        if (this.state.shape !== 'circle') {
            this.props.onCrop(await this.toBlob(croppedCanvas));

            return;
        }

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        if (context) {
            canvas.width = width;
            canvas.height = height;
            context.imageSmoothingEnabled = true;
            context.drawImage(croppedCanvas, 0, 0, width, height);
            context.globalCompositeOperation = 'destination-in';
            context.beginPath();
            context.arc(width / 2, height / 2, Math.min(width, height) / 2, 0, 2 * Math.PI, true);
            context.fill();

            this.props.onCrop(await this.toBlob(canvas));
        }
    }

    toBlob(canvas: HTMLCanvasElement) {
        return new Promise<Blob>((resolve, reject) => canvas.toBlob(result => result ? resolve(result) : reject(), 'image/png'));
    }

    render() {
        return (
            <div className="brax-image-editor">
                <Cropper
                    className={`crop-shape-${this.state.shape}`}
                    ref={this.cropperRef}
                    src={this.props.src}
                    zoomTo={this.state.zoom / 100}
                    dragMode="move"
                    toggleDragModeOnDblclick={false}
                    guides={false}
                    center={false}
                    rotatable={false}
                    zoomOnTouch={false}
                    zoomOnWheel={false}
                    ready={() => this.init()}
                />

                <div>
                    <BraxUncontrolledRangeField
                        label={t('Zoom')}
                        status="success"
                        min={0}
                        max={100}
                        value={this.state.zoom}
                        onChange={value => this.zoom(value)}
                    />

                    <div className="crop-shape">
                        <label>{ t('Formato da seleção') }</label>

                        { this.shapes.map(shape => (
                            <span
                                key={shape}
                                className={this.state.shape === shape ? 'active' : undefined}
                                onClick={() => this.setShape(shape)}
                            />
                        )) }
                    </div>
                </div>
            </div>
        );

    }
};
